import { useCallback, useEffect, useMemo, useState } from 'react';
import { globalToastService } from '@repo/atomic-components';
import type { CheckboxChangeEvent } from '@repo/atomic-components';
import { ChatDocument } from '@/store';
import { useGetDraftDownloadMutation, useLazyGetChatDocumentsQuery } from '@/api';
import type { DraftInfo } from '@/types/chat';
import {
  downloadFilesAsZip,
  downloadWithForcedFilename,
  getDownloadUrl,
  shouldDownloadAsZip,
  triggerProgrammaticDownload,
  zipDownloadFilename,
} from '@/utils';
import { useLazyGetChatDocumentGetOriginalDownloadQuery } from '@/api/chat/chatDocumentGetOriginalDownload';
import { useLazyGetChatDocumentBulkGetDocumentQuery } from '@/api/chat/chatDocumentBulkGetDocument';
import { useGetChatFullDownloadMutation } from '@/api/chat/getChatFullDownload';
import { draftDownloadFilename } from '../utils/draftDownloadFilename';
import { ChatDownloadFilesDialog } from './ChatDownloadFilesDialog';

interface ChatDownloadFilesModalProps {
  visible: boolean;
  onHide: () => void;
  chatId: string;
  files: ChatDocument[];
  /** Opens a draft in the editor panel, the same way clicking it in a message does. */
  onDraftClick: (draft: DraftInfo) => void | Promise<void>;
  /** Opens any other file in the document viewer, the same way clicking its chip in a message does. */
  onDocumentClick: (documentId: string) => void | Promise<void>;
  citedCaseFiles: ChatDocument[];
}

const showDownloadError = (detail: string) =>
  globalToastService.show({
    closable: true,
    severity: 'error',
    summary: 'Download failed',
    detail,
    life: 6000,
  });

export const ChatDownloadFilesModal = ({
  visible,
  onHide,
  chatId,
  files,
  onDraftClick,
  onDocumentClick,
  citedCaseFiles,
}: ChatDownloadFilesModalProps) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isBulkDownloading, setIsBulkDownloading] = useState(false);
  const [isFullChatDownloading, setIsFullChatDownloading] = useState(false);

  const [fetchDocuments, { isFetching: isFetchingDocuments, isUninitialized: isDocumentsFetchUninitialized }] =
    useLazyGetChatDocumentsQuery();
  const [triggerGetOriginalDownload] = useLazyGetChatDocumentGetOriginalDownloadQuery();
  const [triggerBulkGetDocument] = useLazyGetChatDocumentBulkGetDocumentQuery();
  const [triggerFullDownload] = useGetChatFullDownloadMutation();
  const [getDraftDownload] = useGetDraftDownloadMutation();

  useEffect(() => {
    if (visible && chatId) {
      void fetchDocuments({ chatId });
    }
  }, [visible, chatId, fetchDocuments]);

  useEffect(() => {
    if (!visible) {
      setSelectedIds(new Set());
    }
  }, [visible]);

  // The fetch starts in an effect, so treat "not started" as loading to avoid an empty-state flash.
  const isLoadingDocuments = visible && Boolean(chatId) && (isDocumentsFetchUninitialized || isFetchingDocuments);

  const sortedFiles = useMemo(() => {
    return [...files].sort((a, b) => a.documentName.localeCompare(b.documentName, undefined, { sensitivity: 'base' }));
  }, [files]);

  const selectedInSelectable = useMemo(
    () => sortedFiles.filter((d) => selectedIds.has(d.documentId)),
    [sortedFiles, selectedIds],
  );

  const allSelectableSelected = sortedFiles.length > 0 && selectedInSelectable.length === sortedFiles.length;

  const selectAllLabel = allSelectableSelected ? 'Deselect all' : 'Select all';

  const toggleSelectAll = useCallback(
    (event: CheckboxChangeEvent) => {
      const checked = Boolean(event.checked);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (checked) {
          sortedFiles.forEach((d) => next.add(d.documentId));
        } else {
          sortedFiles.forEach((d) => next.delete(d.documentId));
        }
        return next;
      });
    },
    [sortedFiles],
  );

  const toggleOne = useCallback((documentId: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(documentId);
      } else {
        next.delete(documentId);
      }
      return next;
    });
  }, []);

  /**
   * Drafts aren't FletchDocuments, so get the URL from the draft endpoint. The download has no
   * citations, so an empty map is enough.
   */
  const resolveDraftDownloadUrl = useCallback(
    async (doc: ChatDocument) => {
      const { downloadPath, downloadToken } = await getDraftDownload({
        draftVersionId: doc.documentId,
        citationIndexMap: {},
      }).unwrap();
      return getDownloadUrl(downloadPath, downloadToken);
    },
    [getDraftDownload],
  );

  /** Saves a draft on its own, as a .docx named after its title. */
  const fetchDraftDownloadAndTrigger = useCallback(
    async (doc: ChatDocument) => {
      const url = await resolveDraftDownloadUrl(doc);
      if (url) {
        await downloadWithForcedFilename(url, draftDownloadFilename(doc.documentName));
      }
    },
    [resolveDraftDownloadUrl],
  );

  const fetchDownloadAndTrigger = useCallback(
    async (doc: ChatDocument) => {
      if (doc.isDraft) {
        await fetchDraftDownloadAndTrigger(doc);
        return;
      }
      const { downloadPath, downloadToken } = await triggerGetOriginalDownload({
        chatDocumentId: doc.documentId,
      }).unwrap();
      const url = getDownloadUrl(downloadPath, downloadToken);
      if (url) {
        await downloadWithForcedFilename(url, doc.documentName);
      }
    },
    [fetchDraftDownloadAndTrigger, triggerGetOriginalDownload],
  );

  const handleSingleDownload = useCallback(
    async (doc: ChatDocument) => {
      try {
        await fetchDownloadAndTrigger(doc);
      } catch {
        showDownloadError('Could not download this file. Please try again.');
      }
    },
    [fetchDownloadAndTrigger],
  );

  const handleDownloadSelected = useCallback(async () => {
    if (selectedInSelectable.length === 0) {
      return;
    }
    setIsBulkDownloading(true);
    try {
      // `bulk-get-document` 404s the whole request when a draft id is in it, so drafts
      // are fetched one by one from the draft endpoint instead.
      const selectedDrafts = selectedInSelectable.filter((d) => d.isDraft);
      const selectedDocuments = selectedInSelectable.filter((d) => !d.isDraft);

      const bulkItems =
        selectedDocuments.length > 0
          ? (await triggerBulkGetDocument({ documentIds: selectedDocuments.map((d) => d.documentId) }).unwrap())
              .documents
          : [];

      const byId = new Map(bulkItems.map((d) => [d.fletchDocumentId, d] as const));

      // More than two files are downloaded as one archive.
      if (shouldDownloadAsZip(selectedInSelectable.length)) {
        const { failedFilenames } = await downloadFilesAsZip(
          selectedInSelectable.map((doc) => ({
            filename: doc.isDraft ? draftDownloadFilename(doc.documentName) : doc.documentName,
            resolveUrl: async () => {
              if (doc.isDraft) {
                return resolveDraftDownloadUrl(doc);
              }
              const bulkItem = byId.get(doc.documentId);
              return getDownloadUrl(bulkItem?.downloadPath, bulkItem?.downloadToken);
            },
          })),
          zipDownloadFilename('chat documents'),
        );

        // One toast for the run rather than one per file — a chat can hold dozens.
        if (failedFilenames.length === 1) {
          showDownloadError(`Could not download ${failedFilenames[0]}.`);
        } else if (failedFilenames.length > 1) {
          showDownloadError(`Could not download ${failedFilenames.length} of ${selectedInSelectable.length} files.`);
        }
        return;
      }

      for (const doc of selectedDocuments) {
        const bulkItem = byId.get(doc.documentId);
        if (!bulkItem) {
          showDownloadError(`Could not download ${doc.documentName}.`);
          continue;
        }

        const url = getDownloadUrl(bulkItem.downloadPath, bulkItem.downloadToken);
        if (!url) {
          showDownloadError(`Could not download ${doc.documentName}.`);
          continue;
        }

        try {
          await downloadWithForcedFilename(url, doc.documentName);
        } catch {
          showDownloadError(`Could not download ${doc.documentName}.`);
        }
      }

      for (const draft of selectedDrafts) {
        try {
          await fetchDraftDownloadAndTrigger(draft);
        } catch {
          showDownloadError(`Could not download ${draft.documentName}.`);
        }
      }
    } finally {
      setIsBulkDownloading(false);
    }
  }, [selectedInSelectable, triggerBulkGetDocument, fetchDraftDownloadAndTrigger, resolveDraftDownloadUrl]);

  const handleDownloadFullChat = useCallback(async () => {
    if (!chatId) {
      return;
    }
    setIsFullChatDownloading(true);
    try {
      const { downloadPath, downloadToken } = await triggerFullDownload({ chatId }).unwrap();
      const url = getDownloadUrl(downloadPath, downloadToken);
      if (url) {
        triggerProgrammaticDownload(url, 'chat-documents.zip');
      }
    } catch {
      showDownloadError('Could not download the full chat. Please try again.');
    } finally {
      setIsFullChatDownloading(false);
    }
  }, [chatId, triggerFullDownload]);

  /** A draft opens in the editor panel; anything else opens in the document viewer. */
  const handleViewSingle = async (doc: ChatDocument) => {
    if (doc.isDraft) {
      await onDraftClick({ draftVersionId: doc.documentId, title: doc.documentName });
    } else {
      await onDocumentClick(doc.documentId);
    }
    onHide();
  };

  return (
    <ChatDownloadFilesDialog
      visible={visible}
      onHide={onHide}
      sortedFiles={sortedFiles}
      selectedIds={selectedIds}
      citedCaseFiles={citedCaseFiles}
      allSelectableSelected={allSelectableSelected}
      selectAllLabel={selectAllLabel}
      selectedSelectableCount={selectedInSelectable.length}
      isBulkDownloading={isBulkDownloading}
      isFullChatDownloading={isFullChatDownloading}
      isLoadingDocuments={isLoadingDocuments}
      onToggleSelectAll={toggleSelectAll}
      onToggleOne={toggleOne}
      onDownloadSelected={handleDownloadSelected}
      onDownloadFullChat={handleDownloadFullChat}
      onDownloadSingle={handleSingleDownload}
      onViewSingle={handleViewSingle}
    />
  );
};
